import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { useTrading } from '../context/TradingContext'
import { ordersApi } from '../services/api'
import { formatToman, toFaDigits } from '../utils/format'
import { MASKED_PRICE, OrderType } from '../types'
import toast from 'react-hot-toast'

interface AddressForm {
  title: string
  province: string
  city: string
  address: string
  postalCode: string
}

const EMPTY_ADDRESS: AddressForm = { title: '', province: '', city: '', address: '', postalCode: '' }

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams()
  const mode: OrderType = searchParams.get('mode') === 'SELL' ? 'SELL' : 'BUY'
  const isBuy = mode === 'BUY'

  const { itemsOf, unitPriceOf, subtotalOf, shippingOf, totalOf, clearMode } = useCart()
  const { buyBlocked, sellBlocked, pricesHidden } = useTrading()
  const navigate = useNavigate()

  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const items = itemsOf(mode)
  const subtotal = subtotalOf(mode)
  const shipping = shippingOf(mode)
  const total = totalOf(mode)
  const blocked = isBuy ? buyBlocked : sellBlocked

  if (items.length === 0) {
    return (
      <div className="container-main py-16 text-center">
        <p className="text-gray-700 mb-4">
          {isBuy ? 'سبد خرید شما خالی است.' : 'سبد فروش شما خالی است.'}
        </p>
        <Link to="/products" className="btn btn-primary">مشاهده محصولات</Link>
      </div>
    )
  }

  if (blocked) {
    return (
      <div className="container-main py-16 text-center">
        <p className="text-red-700 mb-4">
          {isBuy
            ? 'معاملات فروش موقتاً توسط مدیر متوقف شده است؛ ثبت سفارش خرید ممکن نیست.'
            : 'خرید از مشتریان موقتاً متوقف شده است؛ ثبت سفارش فروش ممکن نیست.'}
        </p>
        <Link to="/cart" className="btn btn-secondary">بازگشت به سبد</Link>
      </div>
    )
  }

  const set = (key: keyof AddressForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setAddress((prev) => ({ ...prev, [key]: e.target.value }))

  const hasAddress =
    address.title && address.province && address.city && address.address && address.postalCode.length === 10

  const submit = async () => {
    if (isBuy && !hasAddress) {
      toast.error('لطفاً آدرس تحویل را کامل وارد کنید')
      return
    }
    if (!isBuy && address.postalCode && address.postalCode.length !== 10) {
      toast.error('کد پستی باید ۱۰ رقم باشد')
      return
    }

    setSubmitting(true)
    try {
      const res = await ordersApi.create({
        type: mode,
        items: items.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
        shippingAddress: hasAddress ? address : undefined,
        notes: notes || undefined
      })
      if (res.data.success && res.data.data) {
        clearMode(mode)
        toast.success(isBuy ? 'سفارش خرید شما ثبت شد' : 'سفارش فروش شما ثبت شد')
        navigate(`/orders/${res.data.data.id}`)
      } else {
        toast.error(res.data.message || 'خطا در ثبت سفارش')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در ثبت سفارش؛ دوباره تلاش کنید')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls = 'input mt-1'

  return (
    <div className="container-main py-10">
      <h1 className="text-2xl font-black text-gray-900 mb-2">تکمیل سفارش</h1>
      <p className="text-sm text-gray-500 mb-6">
        {isBuy
          ? 'این سفارش از نوع «خرید از ما» است — ما فلز به شما می‌فروشیم.'
          : 'این سفارش از نوع «فروش به ما» است — ما فلز را با درصد منفی خرید از شما می‌خریم.'}
      </p>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="font-black text-gray-900 mb-4">۱. اقلام سفارش</h2>
            <div className="space-y-2">
              {items.map(({ product, quantity }) => {
                const unit = unitPriceOf(product, mode)
                const hidden = pricesHidden || product.priceHidden || unit === 0
                return (
                  <div key={product.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                    <span className="font-medium">
                      {product.name} × {toFaDigits(quantity)}
                      <span className="text-xs text-gray-400"> ({toFaDigits(product.weight * quantity)} گرم)</span>
                    </span>
                    <span className="font-bold">{hidden ? MASKED_PRICE : formatToman(unit * quantity)}</span>
                  </div>
                )
              })}
              <div className="flex justify-between pt-2 text-sm">
                <span className="text-gray-600">{isBuy ? 'جمع کالاها' : 'جمع دریافتی شما'}</span>
                <span className="font-bold">{pricesHidden ? MASKED_PRICE : formatToman(subtotal)}</span>
              </div>
              {isBuy && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ارسال</span>
                  <span className="font-bold">{shipping === 0 ? 'رایگان' : formatToman(shipping)}</span>
                </div>
              )}
              <div className="flex justify-between font-black text-gray-900 pt-2 border-t border-gray-200">
                <span>{isBuy ? 'مبلغ کل' : 'مبلغ کل دریافتی'}</span>
                <span className="text-primary-700">{pricesHidden ? MASKED_PRICE : formatToman(total)}</span>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h2 className="font-black text-gray-900 mb-1">۲. اطلاعات گیرنده</h2>
            <p className="text-xs text-gray-500 mb-4">
              {isBuy
                ? 'این آدرس برای تحویل سفارش خرید استفاده می‌شود و الزامی است.'
                : 'اگر مایل به تحویل حضوری هستید، این بخش اختیاری است.'}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">عنوان آدرس {isBuy ? '*' : '(اختیاری)'}</label>
                <input className={inputCls} placeholder="مثلاً منزل" value={address.title} onChange={set('title')} />
              </div>
              <div>
                <label className="label">کد پستی (۱۰ رقم) {isBuy ? '*' : '(اختیاری)'}</label>
                <input className={inputCls} placeholder="مثلاً 1234567890" maxLength={10} value={address.postalCode} onChange={set('postalCode')} />
              </div>
              <div>
                <label className="label">استان {isBuy ? '*' : '(اختیاری)'}</label>
                <input className={inputCls} placeholder="استان تهران" value={address.province} onChange={set('province')} />
              </div>
              <div>
                <label className="label">شهر {isBuy ? '*' : '(اختیاری)'}</label>
                <input className={inputCls} placeholder="تهران" value={address.city} onChange={set('city')} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">نشانی کامل {isBuy ? '*' : '(اختیاری)'}</label>
                <textarea className={`${inputCls} min-h-24`} placeholder="خیابان، کوچه، پلاک، واحد…" value={address.address} onChange={set('address')} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">توضیحات سفارش (اختیاری)</label>
                <textarea className={`${inputCls} min-h-20`} placeholder="توضیحات دلخواه…" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6 h-fit">
          <h2 className="font-black text-gray-900 mb-4">۳. ثبت نهایی</h2>
          <p className="text-xs text-gray-500 leading-6 mb-4">
            با ثبت سفارش، کارشناسان مجموعه برای نهایی‌سازی، پرداخت و هماهنگی تحویل با شما تماس می‌گیرند.
          </p>
          <button className="btn btn-primary w-full !py-3.5 disabled:opacity-50" onClick={submit} disabled={submitting}>
            {submitting ? 'در حال ثبت…' : isBuy ? 'ثبت نهایی سفارش خرید' : 'ثبت نهایی سفارش فروش'}
          </button>
          <Link to="/cart" className="btn btn-secondary w-full !py-2.5 mt-2">بازگشت به سبد</Link>
        </div>
      </div>
    </div>
  )
}

export default Checkout
